import { type IPuEventChecklistResponse, type IPuEventQueueItem } from '../interface/pu-events';

// localStorage persistence for the Election Day Pulse feature, keyed per
// (electionId, puCode). Every access is try/catch-wrapped: when storage is
// unavailable the feature degrades to online-only in-session state.

const queueKey = (electionId: number, puCode: string) => `pu-events:v1:queue:${electionId}:${puCode}`;
const cacheKey = (electionId: number, puCode: string) => `pu-events:v1:cache:${electionId}:${puCode}`;

// Same-page change notification. The widget and the panel each mount their own
// usePulseChecklist instance; a capture made through one must reach the other
// (the `storage` event only fires across tabs). Every write below dispatches
// this event so all instances re-read.
const CHANGED_EVENT = 'pu-events:changed';

const notifyChanged = (electionId: number, puCode: string) => {
	try {
		window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { electionId, puCode } }));
	} catch {
		// Non-browser environment (tests) — nothing to notify.
	}
};

export const subscribePuEventsChanged = (electionId: number, puCode: string, onChange: () => void): (() => void) => {
	const handler = (event: Event) => {
		const detail = (event as CustomEvent<{ electionId: number; puCode: string }>).detail;
		if (detail?.electionId === electionId && detail?.puCode === puCode) onChange();
	};
	window.addEventListener(CHANGED_EVENT, handler);
	return () => window.removeEventListener(CHANGED_EVENT, handler);
};

export const readQueue = (electionId: number, puCode: string): IPuEventQueueItem[] => {
	try {
		const raw = localStorage.getItem(queueKey(electionId, puCode));
		const parsed = raw ? (JSON.parse(raw) as IPuEventQueueItem[]) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

export const writeQueue = (electionId: number, puCode: string, queue: IPuEventQueueItem[]) => {
	try {
		localStorage.setItem(queueKey(electionId, puCode), JSON.stringify(queue));
	} catch {
		// Storage unavailable — in-session state still covers us.
	}
	notifyChanged(electionId, puCode);
};

export const upsertQueueItem = (electionId: number, puCode: string, item: IPuEventQueueItem) => {
	const queue = readQueue(electionId, puCode);
	const next = queue.some((i) => i.referenceId === item.referenceId)
		? queue.map((i) => (i.referenceId === item.referenceId ? item : i))
		: [...queue, item];
	writeQueue(electionId, puCode, next);
	return next;
};

export const removeQueueItem = (electionId: number, puCode: string, referenceId: string) => {
	const next = readQueue(electionId, puCode).filter((i) => i.referenceId !== referenceId);
	writeQueue(electionId, puCode, next);
	return next;
};

/** Last server checklist snapshot — instant paint before the live refetch. */
export const readChecklistCache = (electionId: number, puCode: string): IPuEventChecklistResponse | null => {
	try {
		const raw = localStorage.getItem(cacheKey(electionId, puCode));
		return raw ? (JSON.parse(raw) as IPuEventChecklistResponse) : null;
	} catch {
		return null;
	}
};

export const writeChecklistCache = (electionId: number, puCode: string, checklist: IPuEventChecklistResponse) => {
	try {
		localStorage.setItem(cacheKey(electionId, puCode), JSON.stringify(checklist));
	} catch {
		// Storage unavailable — the in-memory state still covers us.
	}
	notifyChanged(electionId, puCode);
};
